/**
 * Address lines for invoice headers — aligned with Seller `generate_invoice.js` `parseAddress`
 * so company profile / buyer rows (JSON string, object, or array) render consistently.
 */
export function parseAddressLinesForInvoice(address) {
  if (address == null || address === "") return [];

  if (Array.isArray(address)) {
    return address.map((x) => String(x || "").trim()).filter(Boolean);
  }

  if (typeof address === "string") {
    const t = address.trim();
    if (!t) return [];
    try {
      const parsed = JSON.parse(t);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const addressLines = [];
        if (parsed.address) addressLines.push(String(parsed.address));
        if (parsed.landmark) addressLines.push(String(parsed.landmark));
        if (parsed.city) addressLines.push(String(parsed.city));
        if (parsed.state) addressLines.push(String(parsed.state));
        if (parsed.country) addressLines.push(String(parsed.country));
        if (parsed.pincode) addressLines.push(String(parsed.pincode));
        return addressLines.filter(Boolean);
      }
      if (Array.isArray(parsed)) {
        return parsed.map((x) => String(x || "").trim()).filter(Boolean);
      }
    } catch {
      return t.split(",").map((line) => line.trim()).filter(Boolean);
    }
  }

  if (typeof address === "object" && !Array.isArray(address)) {
    const addressLines = [];
    if (address.address) addressLines.push(String(address.address));
    if (address.landmark) addressLines.push(String(address.landmark));
    if (address.city) addressLines.push(String(address.city));
    if (address.state) addressLines.push(String(address.state));
    if (address.country) addressLines.push(String(address.country));
    if (address.pincode) addressLines.push(String(address.pincode));
    return addressLines.filter(Boolean);
  }

  return [];
}

/** Single block for invoice “Bill from / Bill to” (comma-joined lines or fallback fields). */
export function formatPartyAddressForInvoice(entity) {
  if (!entity || typeof entity !== "object") return "—";

  const nested =
    entity.contact_details && typeof entity.contact_details === "object"
      ? entity.contact_details
      : entity.contactDetails && typeof entity.contactDetails === "object"
        ? entity.contactDetails
        : null;

  const addrRaw =
    entity.address ??
    entity.address_json ??
    nested?.address ??
    entity.address_line1 ??
    entity.addressLine1 ??
    "";

  const fromParsed = parseAddressLinesForInvoice(addrRaw);
  if (fromParsed.length) return fromParsed.join(", ");

  const bits = [
    entity.address_line1 || entity.addressLine1,
    nested?.address_line1,
    entity.city || nested?.city,
    entity.state || nested?.state,
    entity.country || nested?.country,
    entity.postal_code || entity.pincode || nested?.pincode || nested?.postal_code,
  ]
    .map((x) => (x != null ? String(x).trim() : ""))
    .filter(Boolean);

  return bits.length ? bits.join(", ") : "—";
}

/** Address as separate lines for `gi-address-lines` (same parsing as {@link formatPartyAddressForInvoice}). */
export function partyAddressLinesForInvoice(entity) {
  if (!entity || typeof entity !== "object") return [];

  const nested =
    entity.contact_details && typeof entity.contact_details === "object"
      ? entity.contact_details
      : entity.contactDetails && typeof entity.contactDetails === "object"
        ? entity.contactDetails
        : null;

  const addrRaw =
    entity.address ??
    entity.address_json ??
    nested?.address ??
    entity.address_line1 ??
    entity.addressLine1 ??
    "";

  const fromParsed = parseAddressLinesForInvoice(addrRaw);
  if (fromParsed.length) return fromParsed;

  const bits = [
    entity.address_line1 || entity.addressLine1,
    nested?.address_line1,
    entity.city || nested?.city,
    entity.state || nested?.state,
    entity.country || nested?.country,
    entity.postal_code || entity.pincode || nested?.pincode || nested?.postal_code,
  ]
    .map((x) => (x != null ? String(x).trim() : ""))
    .filter(Boolean);

  return bits;
}
